import { isServer } from '~/utils/host';
import { ISalesOrderResponse } from '~/interfaces/get-sales-order';
import { normalizeEmail } from '../utils';

interface EventSubmitPayment extends TealiumEventData {
  event: string;
  event_action: string | number;
  event_category: string;
  event_label: string;
  event_noninteraction: 'true' | 'false';
  order_number: string;
  wmap_type: string;
  wmap_order_total: string | number;
  wmap_amount_due: string | number;
  wmap_paid: string | number;
  wmap_pymt_amt: string | number;
}

type EventWithPayload = EventSubmitPayment;

const sendTealiumLinkEvent = (data: TealiumEventData | EventWithPayload) => {
  if (!isServer) {
    try {
      return window.utag?.link?.(data);
    } catch (error) {
      console.error('Error occurred while sending Tealium link event:', error);
    }
  } else {
    console.log(
      'Error occurred while sending Tealium link event: isServer is true',
    );
  }
};

export const sendTealiumEvent = (data: EventWithPayload | TealiumEventData) => {
  return sendTealiumLinkEvent(data);
};

export const sendTealiumPageView = (data?: Record<string, unknown>) => {
  if (!isServer) {
    try {
      return window.utag?.view?.(data);
    } catch (error) {
      console.error(
        'Error occurred while sending Tealium page view event:',
        error,
      );
    }
  }
};
export function submitPaymentUtag(
  cardType = '',
  saleOrderBalanceData: ISalesOrderResponse,
  paymentAmount: number,
  offerType?: string,
) {
  const { TotalAmount, AmountPaid, AmountDue, SalesId } = saleOrderBalanceData;
  const commonProps = {
    order_number: SalesId,
    wmap_type:
      paymentAmount < AmountDue && cardType !== 'Progressive'
        ? 'Other Amount'
        : 'Outstanding Balance',
    wmap_order_total: TotalAmount,
    wmap_amount_due: AmountDue,
    wmap_paid: AmountPaid,
    wmap_pymt_amt: paymentAmount,
    payment_type: cardType?.toLowerCase(),
    ...(offerType ? { offer_type: offerType.toLowerCase() } : {}),
  };
  sendTealiumEvent({
    event: 'wmap-submit-payment-click',
    event_category: 'Online Pay Events',
    event_action: 'click pay now button',
    event_label: `card type ${cardType?.toLowerCase()}`,
    ...commonProps,
    event_noninteraction: 'false',
  });
}

export function sendConfirmationUtag(
  saleOrderBalanceData: ISalesOrderResponse,
  paymentAmount: number,
  paymentType: string = '',
  offerType?: string,
) {
  const {
    TotalAmount,
    AmountPaid,
    AmountDue,
    SalesId,
    Phone,
    Email,
    AccountNumber,
  } = saleOrderBalanceData;
  const formattedPhone = removesSpecialCharacterFromString(Phone);
  const commonProps = {
    order_number: SalesId,
    wmap_type:
      paymentAmount < AmountDue && paymentType !== 'Progressive'
        ? 'Other Amount'
        : 'Outstanding Balance',
    wmap_order_total: TotalAmount,
    wmap_amount_due: AmountDue,
    wmap_paid: AmountPaid,
    wmap_pymt_amt: paymentAmount,
    master_customer_code: AccountNumber,
    payment_type: paymentType?.toLowerCase(),
    ...(offerType ? { offer_type: offerType.toLowerCase() } : {}),
  };
  sendTealiumEvent({
    event: 'online-pay-capture-customer-details',
    event_category: 'Online Pay Events',
    event_action: 'online pay capture customer details',
    event_label: 'online pay confirmation page',
    ...commonProps,
    ...(Email ? { customer_email: normalizeEmail(Email) } : {}),
    ...(formattedPhone ? { customer_phone_number: formattedPhone } : {}),
    event_noninteraction: 'false',
  });
}

export function sendSynchronyExistingAccountUtag() {
  sendTealiumEvent({
    event: 'existing-account-btn-synchrony-wmap',
    event_category: 'link-use existing account wmap page',
    event_action: 'click wmap use existing account btn',
    event_label: 'financing',
  });
}

export function sendAffirmApplyNowUtag() {
  sendTealiumEvent({
    event: 'apply-now-affirm-wmap',
    event_category: 'link- apply now wmap page',
    event_action: 'click affirm apply now btn',
    event_label: 'financing',
  });
}

export function sendProgressiveLookupUtag() {
  sendTealiumEvent({
    event: 'lookup-lease-btn-progressive-wmap',
    event_category: 'link-lookup lease btn wmap page',
    event_action: 'click wmap progressive lookup lease btn',
    event_label: 'financing',
  });
}
export function sendProgressiveApplyUtag() {
  sendTealiumEvent({
    event: 'apply-now-progressive-wmap',
    event_category: 'link- apply now wmap page',
    event_action: 'click progressive apply now btn',
    event_label: 'financing',
  });
}
export const removesSpecialCharacterFromString = (str: string | undefined) => {
  return str ? `1${str.replace(/[^a-zA-Z0-9 ]/g, '').replace(/\s/g, '')}` : '';
};

// Track sent progressive error events to prevent duplicates
const sentProgressiveErrors = new Set<string>();

export function sendProgressiveErrorUtag(eventLabel: string) {
  // Create a unique key combining event type and label
  const eventKey = `progressive-error:${eventLabel}`;

  // Only send if we haven't already sent this exact error
  if (!sentProgressiveErrors.has(eventKey)) {
    sendTealiumEvent({
      event: 'wmap-progressive-error',
      event_category: 'Online Pay Events',
      event_action: 'progressive error message',
      event_label: eventLabel,
    });
    sentProgressiveErrors.add(eventKey);
  }
}

/**
 * Clear the progressive error tracking cache
 * Useful for testing or when you want to allow re-tracking of the same errors
 */
export function clearProgressiveErrorTracking() {
  sentProgressiveErrors.clear();
}

/**
 * Remove a specific error from tracking cache
 * Allows re-tracking of a specific error type
 */
export function clearSpecificProgressiveError(eventLabel: string) {
  const eventKey = `progressive-error:${eventLabel}`;
  sentProgressiveErrors.delete(eventKey);
}
